/**
 * Dashboard — Konfigurierbares Widget-Dashboard mit KPIs und Schnellzugriff.
 *
 * Features:
 * - Personalisierbare Widget-Auswahl (Drag & Drop, Rollen-basiert)
 * - Quick-Stats, AI-Insights, Top-Performer, Besetzung, System-Health
 * - Tagesgruss und Aufgaben-Widget
 *
 * @page /dashboard
 * @roles admin, manager, mitarbeiter, viewer
 * @api /api/dashboard/config, /api/dashboard/widgets, /api/dashboard/widget-data/:id
 * @api /api/dashboard/ai-insight, /api/dashboard/rolle/:rolle, /api/dashboard/reset
 * @api /api/health
 * @i18n home
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
// Ring and Spark removed — replaced by MiniSparkline + MiniProgressBar
import { Package, Zap, Settings, Plus, RotateCcw, X, Pencil, ClipboardList, ClipboardCheck, Eye, TrendingUp, Mail, Star, UserCheck, UserPlus, ArrowRight, CheckCircle, Users, SmilePlus } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import {
  IconForecast, IconSchicht, IconRotation, IconPerformance,
  IconFeiertage, IconRohdaten, IconApi, IconKostenstellen,
  IconMitarbeiter,
} from '../components/Icons';
import { Button, Badge, Modal, PageHeader, KpiCard } from '../components/ui';
import TaskWidget from '../components/TaskWidget';

const API = import.meta.env.VITE_API_URL || '';

function getGreeting(t) {
  const h = new Date().getHours();
  if (h < 12) return t('home.greeting.morning');
  if (h < 18) return t('home.greeting.afternoon');
  return t('home.greeting.evening');
}

function fmt(n, locale) {
  if (n == null || n === 0) return '0';
  return n.toLocaleString(locale);
}
function fmtEur(n, locale) {
  if (n == null || n === 0) return '0 €';
  const abs = Math.abs(n);
  const s = abs >= 1000
    ? (abs / 1000).toLocaleString(locale, { maximumFractionDigits: 1 }) + 'K €'
    : abs.toLocaleString(locale, { maximumFractionDigits: 0 }) + ' €';
  return n < 0 ? `−${s}` : s;
}
function fmtPct(n, locale) {
  if (n == null) return '0 %';
  return n.toLocaleString(locale, { maximumFractionDigits: 1 }) + ' %';
}

/* ══════════════════════════════════════════════════════════════
   WIDGET COMPONENTS
   ══════════════════════════════════════════════════════════════ */

const WIDGET_META = {
  forecast:        { icon: IconForecast,     color: '#338edc', to: '/forecast' },
  schichtplanung:  { icon: IconSchicht,      color: '#4D9CE0', to: '/schichtplanung' },
  kunden_rotation: { icon: IconRotation,     color: '#06b6d4', to: '/kunden-rotation' },
  performance:     { icon: IconPerformance,  color: '#338edc', to: '/performance' },
  feiertage:       { icon: IconFeiertage,    color: '#F59E0B', to: '/feiertage' },
  mitarbeiter:     { icon: IconMitarbeiter,  color: '#6366F1', to: '/mitarbeiter' },
  rohdaten:        { icon: IconRohdaten,     color: '#6366F1', to: '/rohdaten' },
  api_config:      { icon: IconApi,          color: '#22D3EE', to: '/api-config' },
  segmentanalyse:  { icon: IconKostenstellen,color: '#6366F1', to: '/kostenstellen' },
  quick_stats:     { icon: null,             color: '#6B7280', to: null },
  ai_insight:      { icon: null,             color: '#338edc', to: null },
  top_performer:   { icon: IconPerformance,  color: '#338edc', to: '/performance' },
  besetzung:       { icon: IconSchicht,      color: '#4D9CE0', to: '/schichtplanung' },
  system_health:   { icon: null,             color: '#338edc', to: null },
  notifications:   { icon: null,             color: '#F59E0B', to: '/notifications' },
  forecast_chart:  { icon: IconForecast,     color: '#338edc', to: '/forecast' },
  operations_heute:{ icon: Package,           color: '#338edc', to: '/operations' },
  durchsatz:       { icon: Zap,              color: '#22D3EE', to: '/operations' },
  forecast_deadlines:{ icon: ClipboardCheck,  color: '#338edc', to: '/forecast-review' },
  cases_overview:  { icon: ClipboardList,    color: '#338edc', to: '/cases/liste' },
  cases_watchlist: { icon: Eye,              color: '#6366F1', to: '/cases/liste' },
  cases_trend:     { icon: TrendingUp,       color: '#338edc', to: '/cases/liste' },
  email_inbox_today:{ icon: Mail,            color: '#F59E0B', to: '/cases/liste' },
  satisfaction_score:{ icon: Star,           color: '#338edc', to: '/zufriedenheit' },
  active_substitutions:{ icon: UserCheck,    color: '#6366F1', to: '/cases/konfiguration' },
  flex_pool:           { icon: UserPlus,     color: '#338edc', to: '/flex/bewerber' },
  pack_timer:          { icon: Package,      color: '#338edc', to: '/packplatz-timer' },
};

const WIDGET_LABEL_KEYS = {
  forecast: 'home.widget.forecast', schichtplanung: 'home.widget.schichtplanung', kunden_rotation: 'home.widget.kunden_rotation',
  performance: 'home.widget.performance', feiertage: 'home.widget.feiertage', mitarbeiter: 'home.widget.mitarbeiter',
  rohdaten: 'home.widget.rohdaten', api_config: 'home.widget.api_config', segmentanalyse: 'home.widget.segmentanalyse',
  quick_stats: 'home.widget.quick_stats', ai_insight: 'home.widget.ai_insight', top_performer: 'home.widget.top_performer',
  besetzung: 'home.widget.besetzung', system_health: 'home.widget.system_health', notifications: 'home.widget.notifications',
  forecast_chart: 'home.widget.forecast_chart',
  operations_heute: 'home.widget.operations_heute', durchsatz: 'home.widget.durchsatz',
  forecast_deadlines: 'home.widget.forecast_deadlines',
  cases_overview: 'home.widget.cases_overview', cases_watchlist: 'home.widget.cases_watchlist',
  cases_trend: 'home.widget.cases_trend', email_inbox_today: 'home.widget.email_inbox_today',
  satisfaction_score: 'home.widget.satisfaction_score', active_substitutions: 'home.widget.active_substitutions',
  flex_pool: 'home.widget.flex_pool',
  pack_timer: 'home.widget.pack_timer',
};

const CATEGORY_LABEL_KEYS = {
  planung: 'home.category.planung', team: 'home.category.team', controlling: 'home.category.controlling',
  system: 'home.category.system', intelligence: 'home.category.intelligence', operations: 'home.category.operations',
  cases: 'home.category.cases', hr: 'home.category.hr',
};

// Loading skeleton
function WidgetSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      <div className="h-3 bg-surface-inset rounded w-2/3" />
      <div className="h-8 bg-surface-inset rounded w-1/2" />
      <div className="h-2 bg-surface-inset rounded w-full" />
    </div>
  );
}

// Small stat helper
function StatLabel({ label, value, color }) {
  return (
    <div>
      <div className="text-[11px] text-[var(--text-secondary)] font-medium uppercase tracking-wider">{label}</div>
      <div className="font-mono font-bold text-sm text-content">{value}</div>
    </div>
  );
}

// Score bar
function ScoreBar({ name, score, maxWidth }) {
  const color = '#338edc';
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-[var(--text-secondary)] truncate" style={{ width: maxWidth || 48 }}>{name}</span>
      <div className="flex-1 h-1 bg-surface-inset rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(score, 120) / 1.2}%`, backgroundColor: color }} />
      </div>
      <span className="text-[11px] font-mono font-bold min-w-[36px] text-right text-content">{score}%</span>
    </div>
  );
}

/* ── Reusable Widget Helpers ── */

function MiniSparkline({ data, dataKey = 'value', color = '#338edc', height = 40 }) {
  if (!data?.length || data.length < 2) return null;
  const chartData = typeof data[0] === 'number' ? data.map(v => ({ value: v })) : data;
  const key = typeof data[0] === 'number' ? 'value' : dataKey;
  const gradId = `spark-${color.replace('#', '')}`;
  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.15} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey={key} stroke={color} strokeWidth={1.5} fill={`url(#${gradId})`} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function MiniProgressBar({ value, max, label, color = '#338edc', showPct = false }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div>
      {(label || showPct) && (
        <div className="flex items-center justify-between mb-1">
          {label && <span className="text-[11px] text-[var(--text-secondary)]">{label}</span>}
          {showPct && <span className="text-[11px] font-mono font-bold text-content">{pct}%</span>}
        </div>
      )}
      <div className="h-1.5 bg-surface-inset rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function HeroKPI({ value, label, trend, trendUp }) {
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-mono font-extrabold text-content">{value}</span>
        {trend != null && (
          <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${trendUp ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}`}>
            {trend}
          </span>
        )}
      </div>
      {label && <div className="text-[11px] text-[var(--text-secondary)] mt-0.5">{label}</div>}
    </div>
  );
}

/* Individual widget renderers */
function WidgetForecast({ data, t, locale }) {
  if (!data) return <WidgetSkeleton />;
  return (
    <div className="flex flex-col h-full">
      <HeroKPI value={fmtPct(data.erfuellung, locale)} label={t('home.fulfillment')} />
      <div className="flex gap-4 mt-1.5 text-[11px]">
        <span className="text-[var(--text-secondary)]">{t('common.target')}: <span className="font-mono font-bold text-content">{fmt(data.soll, locale)}</span></span>
        <span className="text-[var(--text-secondary)]">{t('common.actual')}: <span className="font-mono font-bold text-content">{data.ist > 0 ? fmt(data.ist, locale) : '–'}</span></span>
      </div>
      {data.soll > 0 && <div className="mt-2"><MiniProgressBar value={data.ist} max={data.soll} /></div>}
      <div className="flex-1 mt-auto pt-1 min-h-0">
        <MiniSparkline data={data.spark} height={36} />
      </div>
    </div>
  );
}

function WidgetSchichtplanung({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  const maxRole = Math.max(data.picker || 0, data.packer || 0, 1);
  return (
    <div className="flex flex-col h-full">
      <HeroKPI value={`${data.besetzungsrate}%`} label={t('home.occupancyRate')} />
      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-[var(--text-secondary)] w-12">{t('home.picker')}</span>
          <div className="flex-1 h-1.5 bg-surface-inset rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${(data.picker / maxRole) * 100}%`, backgroundColor: '#338edc' }} />
          </div>
          <span className="text-[11px] font-mono font-bold text-content w-6 text-right">{data.picker}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-[var(--text-secondary)] w-12">{t('home.packer')}</span>
          <div className="flex-1 h-1.5 bg-surface-inset rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${(data.packer / maxRole) * 100}%`, backgroundColor: '#22D3EE' }} />
          </div>
          <span className="text-[11px] font-mono font-bold text-content w-6 text-right">{data.packer}</span>
        </div>
      </div>
    </div>
  );
}

function WidgetKundenRotation({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  const hasData = data.avg_score > 0 || data.work_slots > 0;
  if (!hasData) {
    return (
      <div className="flex items-center gap-2 text-[var(--text-tertiary)] opacity-60">
        <IconRotation style={{ width: 14, height: 14 }} />
        <span className="text-xs">{t('home.no_rotation_data')}</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col h-full">
      <HeroKPI value={`${data.avg_score}%`} label={t('home.utilization')} />
      <div className="mt-2">
        <MiniProgressBar value={data.avg_score} max={100} color="#06b6d4" />
      </div>
      <div className="mt-auto pt-2 flex gap-4 text-[11px] text-[var(--text-secondary)]">
        <span>{t('home.slots')}: <span className="font-mono font-bold text-content">{data.work_slots}</span></span>
        <span>{t('common.customers')}: <span className="font-mono font-bold text-content">{data.kunden_count}</span></span>
      </div>
    </div>
  );
}

function WidgetPerformance({ data, t, locale }) {
  if (!data) return <WidgetSkeleton />;
  return (
    <div className="flex flex-col h-full">
      <HeroKPI value={data.articles_per_hour || '–'} label={t('home.articles_per_hour_short')} />
      <div className="grid grid-cols-3 gap-2 mt-3">
        <StatLabel label={t('home.waves')} value={data.waves_today || 0} />
        <StatLabel label={t('home.articles')} value={(data.articles_today || 0).toLocaleString(locale)} />
        <StatLabel label={t('home.employees_active')} value={data.employees_today || 0} />
      </div>
    </div>
  );
}

function WidgetFeiertage({ data, t, tMonth, locale }) {
  if (!data) return <WidgetSkeleton />;
  return (
    <div className="flex flex-col h-full">
      {data.next_name ? (
        <>
          <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{t('home.nextHoliday')}</div>
          <div className="text-sm font-semibold text-content mt-0.5">{data.next_name}</div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-lg font-mono font-extrabold text-content">{t('home.inDays', { days: data.days_until })}</span>
            {data.next_datum && (
              <span className="text-[11px] text-[var(--text-tertiary)]">
                {new Date(data.next_datum).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })}
              </span>
            )}
          </div>
        </>
      ) : (
        <div className="text-xs text-[var(--text-tertiary)]">{t('home.noUpcomingHolidays')}</div>
      )}
      <div className="mt-auto pt-2 text-[11px] text-[var(--text-secondary)]">
        {tMonth(data.monat - 1)}: <span className="font-mono font-bold text-content">{data.count}</span>
      </div>
    </div>
  );
}

function WidgetMitarbeiter({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  return (
    <div className="flex flex-col items-center justify-center h-full gap-1">
      <Users size={20} className="text-[#8B5CF6] mb-1" />
      <div className="text-[32px] font-mono font-extrabold text-content leading-none">{data.count}</div>
      <div className="text-[11px] text-[var(--text-secondary)] font-medium uppercase tracking-wider">{t('home.active_employees')}</div>
    </div>
  );
}

function WidgetRohdaten({ t }) {
  return (
    <div className="text-xs text-[var(--text-secondary)]">{t('home.db_tables_export')}</div>
  );
}

function WidgetApiConfig({ t }) {
  return (
    <div className="text-xs text-[var(--text-secondary)]">{t('home.odoo_sync')}</div>
  );
}

function WidgetQuickStats({ data, t, locale }) {
  if (!data) return <WidgetSkeleton />;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#338edc' }} />
        <span className="text-xs text-[var(--text-secondary)] flex-1">{t('common.packages')}</span>
        <span className="text-xs font-mono font-bold text-content">{fmt(data.soll, locale)} / {fmt(data.ist, locale)}</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#10B981' }} />
        <span className="text-xs text-[var(--text-secondary)] flex-1">{t('home.score')}</span>
        <span className="text-xs font-mono font-bold text-content">{data.score > 0 ? `${data.score}%` : '–'}</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#6366F1' }} />
        <span className="text-xs text-[var(--text-secondary)] flex-1">{t('home.team_label')}</span>
        <span className="text-xs font-mono font-bold text-content">{data.team > 0 ? t('home.team_employees', { n: data.team }) : '–'}</span>
      </div>
    </div>
  );
}

function WidgetAiInsight({ data, onRefresh, t }) {
  if (!data) return <WidgetSkeleton />;
  const hasInsight = data.insight && data.insight.length > 0;
  if (!hasInsight) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
        <span className="text-[var(--text-tertiary)] text-xs">{t('home.noInsights')}</span>
        {onRefresh && (
          <button onClick={onRefresh} className="text-[11px] text-primary-500 hover:text-primary-600 transition-colors font-medium">
            {t('home.regenerate')}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col h-full">
      <p className="text-sm text-content-secondary leading-relaxed flex-1">{data.insight}</p>
      {onRefresh && (
        <button onClick={onRefresh} className="text-[11px] text-[var(--text-tertiary)] hover:text-primary-500 transition-colors font-mono mt-2 self-start">
          {t('home.regenerate')}
        </button>
      )}
    </div>
  );
}

function WidgetTopPerformer({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  if (!data.top?.length) {
    return (
      <div className="flex items-center gap-2 text-[var(--text-tertiary)] opacity-60">
        <IconPerformance style={{ width: 14, height: 14 }} />
        <span className="text-xs">{t('common.no_data')}</span>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {data.top.slice(0, 3).map((m, i) => (
        <div key={m.name} className="flex items-center gap-2">
          <span className="text-[11px] font-mono text-[var(--text-tertiary)] w-4">{i + 1}.</span>
          <ScoreBar name={m.name} score={m.score} />
        </div>
      ))}
    </div>
  );
}

function WidgetBesetzung({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  return (
    <div className="flex flex-col h-full">
      <HeroKPI value={data.team} label={t('home.widget.teamToday')} />
      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-[var(--text-secondary)] w-12">{t('home.picker')}</span>
          <div className="flex-1 h-1.5 bg-surface-inset rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${data.team > 0 ? (data.picker / data.team) * 100 : 0}%`, backgroundColor: '#338edc' }} />
          </div>
          <span className="text-[11px] font-mono font-bold text-content w-6 text-right">{data.picker}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-[var(--text-secondary)] w-12">{t('home.packer')}</span>
          <div className="flex-1 h-1.5 bg-surface-inset rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${data.team > 0 ? (data.packer / data.team) * 100 : 0}%`, backgroundColor: '#22D3EE' }} />
          </div>
          <span className="text-[11px] font-mono font-bold text-content w-6 text-right">{data.packer}</span>
        </div>
      </div>
    </div>
  );
}

function WidgetSystemHealth({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  const dbOk = data.db === 'ok';
  const apiOk = data.api === 'ok';
  return (
    <div className="flex flex-col justify-center h-full">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${dbOk ? 'bg-emerald-500' : 'bg-red-400'}`} />
          <span className="text-xs text-[var(--text-secondary)]">DB</span>
          <span className="text-xs font-mono font-bold text-content">{dbOk ? 'OK' : t('home.problem_detected')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${apiOk ? 'bg-emerald-500' : 'bg-red-400'}`} />
          <span className="text-xs text-[var(--text-secondary)]">API</span>
          <span className="text-xs font-mono font-bold text-content">{apiOk ? 'OK' : t('home.problem_detected')}</span>
        </div>
      </div>
    </div>
  );
}

function WidgetNotifications({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  if (!data.items?.length) return <div className="text-xs text-[var(--text-tertiary)]">{t('home.no_new_notifications')}</div>;
  const timeAgo = (d) => {
    if (!d) return '';
    const diff = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (diff < 60) return `${diff}m`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h`;
    return `${Math.floor(diff / 1440)}d`;
  };
  return (
    <div className="space-y-2">
      {data.items.slice(0, 3).map(n => (
        <div key={n.id} className="flex items-start gap-2 text-xs">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-content-secondary truncate">{n.titel}</div>
            <div className="text-[var(--text-tertiary)] truncate">{n.nachricht}</div>
          </div>
          {n.created_at && <span className="text-[10px] text-[var(--text-tertiary)] flex-shrink-0">{timeAgo(n.created_at)}</span>}
        </div>
      ))}
    </div>
  );
}

function WidgetForecastChart({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  if (!data.days?.length) return <div className="text-xs text-[var(--text-tertiary)]">{t('home.no_forecast_data')}</div>;
  const maxVal = Math.max(...data.days.map(d => Math.max(d.soll, d.ist)), 1);
  return (
    <div className="flex flex-col h-full">
      <div className="flex gap-3 text-[10px] text-[var(--text-tertiary)] mb-2">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-primary-500 inline-block" /> {t('common.target')}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-cyan-400 inline-block" /> {t('common.actual')}</span>
      </div>
      <div className="flex items-end gap-1 flex-1 min-h-[80px]">
        {data.days.map((d, i) => (
          <div key={i} className="flex-1 flex gap-px justify-center h-full" title={`${t('common.target')}: ${d.soll} / ${t('common.actual')}: ${d.ist}`}>
            <div className="w-2 bg-primary-500/70 rounded-t self-end transition-all" style={{ height: `${(d.soll / maxVal) * 100}%` }} />
            {d.ist > 0
              ? <div className="w-2 bg-cyan-400/80 rounded-t self-end transition-all" style={{ height: `${(d.ist / maxVal) * 100}%` }} />
              : <div className="w-2 rounded-t self-end border border-dashed border-cyan-400/40" style={{ height: `${(d.soll / maxVal) * 100}%` }} />
            }
          </div>
        ))}
      </div>
    </div>
  );
}

function WidgetOperationsHeute({ data, t, locale }) {
  if (!data) return <WidgetSkeleton />;
  const soll = data.soll_pakete_heute || 0;
  const ist = data.ist_pakete_heute || 0;
  const pct = soll > 0 ? Math.round((ist / soll) * 100) : 0;
  return (
    <div className="flex flex-col h-full justify-between">
      <div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-mono font-extrabold text-content">{fmt(ist, locale)}</span>
          <span className="text-[11px] text-[var(--text-secondary)]">/ {fmt(soll, locale)} {t('common.target')}</span>
          {soll > 0 && <span className="text-xs font-bold ml-auto px-1.5 py-0.5 rounded bg-primary-500/10 text-primary-600">{pct}%</span>}
        </div>
      </div>
      {soll > 0 && <MiniProgressBar value={ist} max={soll} />}
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-[var(--text-secondary)]">{t('home.open_orders', { n: fmt(data.offene_auftraege, locale) })}</span>
        {data.pakete_gestern > 0 && <span className="text-[var(--text-secondary)]">{t('home.yesterday', { n: fmt(data.pakete_gestern, locale) })}</span>}
      </div>
    </div>
  );
}

function WidgetDurchsatz({ data, t, locale }) {
  if (!data) return <WidgetSkeleton />;
  const pph = data.pakete_pro_stunde || 0;
  const avg = data.durchschnitt_7_tage || 0;
  const spark = data.sparkline_data || [];
  const trendUp = pph * 8 >= avg;
  return (
    <div className="flex flex-col h-full">
      <HeroKPI value={fmt(pph, locale)} label={t('home.packages_per_hour')} trend={trendUp ? '↑' : '↓'} trendUp={trendUp} />
      <div className="flex-1 mt-2 min-h-0">
        <MiniSparkline data={spark} height={44} />
      </div>
      <div className="text-[11px] text-[var(--text-secondary)]">
        {t('home.avg_7_days', { n: fmt(avg, locale) })}
      </div>
    </div>
  );
}

/* ── Cases & Support Widgets ── */

function WidgetCasesOverview({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  const total = (data.sla_ok || 0) + (data.sla_warning || 0) + (data.sla_breached || 0);
  return (
    <div className="flex flex-col h-full">
      <HeroKPI value={data.open_total} label={t('home.widget.open')} />
      {total > 0 && (
        <div className="mt-3 flex h-2 rounded-full overflow-hidden bg-surface-inset">
          {data.sla_ok > 0 && <div className="h-full bg-emerald-500" style={{ width: `${(data.sla_ok / total) * 100}%` }} />}
          {data.sla_warning > 0 && <div className="h-full bg-amber-400" style={{ width: `${(data.sla_warning / total) * 100}%` }} />}
          {data.sla_breached > 0 && <div className="h-full bg-red-500" style={{ width: `${(data.sla_breached / total) * 100}%` }} />}
        </div>
      )}
      <div className="flex gap-3 mt-2 text-[11px]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /><span className="font-mono font-bold text-content">{data.sla_ok}</span></span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /><span className="font-mono font-bold text-content">{data.sla_warning}</span></span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /><span className="font-mono font-bold text-content">{data.sla_breached}</span></span>
      </div>
      <div className="flex gap-2 mt-auto pt-1 text-[11px]">
        <Badge variant="default" className="text-[10px]">{t('home.widget.waiting')}: {data.waiting}</Badge>
        <Badge variant="default" className="text-[10px]">{t('home.widget.inProgress')}: {data.in_progress}</Badge>
      </div>
    </div>
  );
}

function WidgetCasesWatchlist({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  if (!data.cases?.length) {
    return (
      <div className="flex items-center gap-2 text-[var(--text-tertiary)] opacity-60">
        <Eye className="w-4 h-4" />
        <span className="text-xs">{t('widgets.noWatchedCases')}</span>
      </div>
    );
  }
  const stageColors = { waiting_for_support: '#F59E0B', in_progress: '#338edc', solution_proposed: '#10B981', closed: '#9CA3AF' };
  const timeAgo = (d) => {
    if (!d) return '–';
    const diff = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (diff < 60) return `${diff}m`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h`;
    return `${Math.floor(diff / 1440)}d`;
  };
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-1.5 overflow-hidden">
        {data.cases.slice(0, 5).map(c => (
          <div key={c.case_id} className="flex items-center gap-2 py-0.5 rounded hover:bg-surface-hover transition-colors cursor-pointer">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: stageColors[c.stage] || '#9CA3AF' }} />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-content truncate">#{c.case_id} {c.title || '–'}</div>
            </div>
            <span className="text-[10px] text-[var(--text-tertiary)] flex-shrink-0">{timeAgo(c.updated_at)}</span>
          </div>
        ))}
      </div>
      {data.count > 5 && (
        <div className="text-[11px] text-[var(--text-secondary)] font-medium pt-2 mt-auto">
          {t('widgets.showAll')} ({data.count}) →
        </div>
      )}
    </div>
  );
}

function WidgetCasesTrend({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  const trendPct = typeof data.trend_pct === 'number' && isFinite(data.trend_pct) ? data.trend_pct : null;
  const hasResponseTime = data.avg_response_hours != null && data.avg_response_hours !== '';
  return (
    <div className="flex flex-col h-full">
      <HeroKPI
        value={hasResponseTime ? `${data.avg_response_hours}h` : '–'}
        label={t('widgets.avgResponseTime')}
        trend={trendPct != null ? `${trendPct <= 0 ? '↓' : '↑'} ${Math.abs(trendPct)}%` : null}
        trendUp={trendPct != null ? trendPct <= 0 : false}
      />
      <div className="flex-1 mt-2 min-h-0">
        <MiniSparkline data={data.sparkline} dataKey="count" height={40} />
      </div>
      <div className="flex gap-4 text-[11px]">
        <span className="text-[var(--text-secondary)]">{t('widgets.newCases')}: <span className="font-mono font-bold text-content">{data.new_cases ?? 0}</span></span>
        <span className="text-[var(--text-secondary)]">{t('widgets.closedCases')}: <span className="font-mono font-bold text-content">{data.closed_cases ?? 0}</span></span>
      </div>
    </div>
  );
}

function WidgetEmailToday({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  return (
    <div className="flex flex-col h-full">
      <HeroKPI value={data.received} label={t('widgets.received')} />
      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-[var(--text-secondary)]">{t('widgets.assigned')}</span>
          <span className="font-mono font-bold text-content">{data.assigned}</span>
        </div>
        <div className="flex items-center justify-between text-[11px]">
          <span className={data.unassigned > 0 ? 'text-amber-500 font-semibold' : 'text-[var(--text-secondary)]'}>{t('widgets.unassigned')}</span>
          <span className={`font-mono font-bold ${data.unassigned > 0 ? 'text-amber-500' : 'text-content'}`}>{data.unassigned}</span>
        </div>
      </div>
      {data.received > 0 && (
        <div className="mt-auto pt-2">
          <MiniProgressBar value={data.assigned} max={data.received} color="#338edc" />
        </div>
      )}
    </div>
  );
}

function WidgetSatisfaction({ data, t, locale }) {
  if (!data) return <WidgetSkeleton />;
  if (data.empty) {
    return (
      <div className="flex items-center gap-2 text-[var(--text-tertiary)] opacity-60">
        <Star className="w-4 h-4" />
        <span className="text-xs">{t('widgets.noSurveys')}</span>
      </div>
    );
  }
  const score = data.score || 0;
  const fullStars = Math.floor(score);
  const emptyStars = 5 - fullStars;
  const fragenWithAvg = (data.fragen || []).filter(f => f.avg != null);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SmilePlus size={20} className="text-[#338edc]" />
          <span className="text-[28px] font-mono font-extrabold leading-none text-content">{score}</span>
          <span className="text-xs text-[var(--text-tertiary)]">/ 5</span>
          <div className="flex gap-0.5 ml-1">
            {Array.from({ length: fullStars }, (_, i) => (
              <Star key={`f${i}`} className="w-3.5 h-3.5 text-primary-500 fill-primary-500" />
            ))}
            {Array.from({ length: emptyStars }, (_, i) => (
              <Star key={`e${i}`} className="w-3.5 h-3.5 text-[var(--text-tertiary,#6B7280)]" />
            ))}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{t('widgets.responseRate')}</div>
          <div className="text-sm font-mono font-bold text-content">{data.response_rate}%</div>
        </div>
      </div>
      {data.trend != null && (
        <div className="text-[11px]">
          <span className={data.trend >= 0 ? 'text-emerald-500 font-semibold' : 'text-red-500 font-semibold'}>
            {data.trend >= 0 ? '↑' : '↓'} {Math.abs(data.trend)}
          </span>
          <span className="text-[var(--text-tertiary)] ml-1">{t('home.widget.vs_prev_month')}</span>
        </div>
      )}
      <div className="flex gap-3 text-[11px] flex-wrap">
        {fragenWithAvg.map(f => (
          <span key={f.id} className="text-[var(--text-secondary)]">
            {locale === 'de' ? f.label_de : f.label_en}: <span className="font-mono font-bold text-content">{f.avg}</span>
          </span>
        ))}
      </div>
      {data.responses > 0 && (
        <div className="text-[10px] text-[var(--text-tertiary)]">{data.responses} {t('home.widget.satisfaction_responses')}</div>
      )}
    </div>
  );
}

function WidgetActiveSubstitutions({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  if (!data.items?.length) {
    return (
      <div className="flex items-center gap-2 text-[var(--text-tertiary)] opacity-60">
        <CheckCircle className="w-4 h-4 text-emerald-400" />
        <span className="text-xs">{t('widgets.noSubstitutions')}</span>
      </div>
    );
  }
  const shortName = (name) => {
    if (!name) return '–';
    const parts = name.trim().split(' ');
    return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
  };
  return (
    <div className="space-y-2">
      {data.items.slice(0, 3).map(v => (
        <div key={v.id} className="flex items-center gap-1.5 text-xs">
          <span className="font-semibold text-content truncate">{shortName(v.abwesend_name)}</span>
          <ArrowRight className="w-3 h-3 text-[var(--text-tertiary)] flex-shrink-0" />
          <span className="font-semibold text-content truncate">{shortName(v.vertreter_name)}</span>
          <span className="text-[10px] text-[var(--text-tertiary)] ml-auto flex-shrink-0">
            {v.permanent ? t('home.widget.permanent') : v.gueltig_bis ? new Date(v.gueltig_bis).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : ''}
          </span>
        </div>
      ))}
    </div>
  );
}

function WidgetSegmentanalyse({ data, t, locale }) {
  if (data == null || data.error) return <WidgetSkeleton />;
  const top3 = (data.segmente || [])
    .filter(s => s.umsatz !== 0 || s.kosten !== 0)
    .sort((a, b) => b.saldo - a.saldo)
    .slice(0, 3);
  const g = data.gesamt || {};
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-end gap-6">
        <div>
          <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{t('home.saldo')}</div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-mono font-extrabold text-content">{fmtEur(g.saldo, locale)}</span>
            {g.saldo != null && (
              <span className={`text-[10px] font-bold ${g.saldo >= 0 ? 'text-emerald-500' : 'text-amber-500'}`}>{g.saldo >= 0 ? '↑' : '↓'}</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{t('home.marge')}</div>
          <span className="text-lg font-mono font-extrabold text-content">{fmtPct(g.marge_pct, locale)}</span>
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        {top3.map(seg => {
          const maxAbs = Math.max(...top3.map(s => Math.abs(s.saldo)), 1);
          const barPct = Math.min((Math.abs(seg.saldo) / maxAbs) * 100, 100);
          const barColor = seg.saldo >= 0 ? '#10B981' : '#EF4444';
          return (
            <div key={seg.id} className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: barColor }} />
              <span className="text-[11px] text-[var(--text-secondary)] w-24 truncate">{seg.name}</span>
              <div className="flex-1 h-1.5 bg-surface-inset rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${barPct}%`, backgroundColor: barColor, opacity: 0.7 }} />
              </div>
              <span className="text-[11px] font-mono font-bold min-w-[52px] text-right text-content">
                {fmtEur(seg.saldo, locale)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WidgetForecastDeadlines({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  const statusColors = { submitted: '#F59E0B', draft: '#9CA3AF', overdue: '#EF4444', approved: '#10B981' };
  const statusLabels = { submitted: t('home.widget.forecastSubmitted'), draft: t('home.widget.forecastPending'), overdue: t('home.widget.forecastOverdue'), approved: t('home.widget.forecastApproved') };
  return (
    <div className="flex flex-col h-full">
      <HeroKPI value={data.submitted || 0} label={t('home.widget.toReview')} />
      <div className="flex gap-3 mt-2 text-[11px]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColors.draft }} /><span className="font-mono font-bold text-content">{data.pending || 0}</span> <span className="text-[var(--text-tertiary)]">{t('home.widget.forecastPending')}</span></span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColors.overdue }} /><span className="font-mono font-bold text-content">{data.overdue || 0}</span> <span className="text-[var(--text-tertiary)]">{t('home.widget.forecastOverdue')}</span></span>
      </div>
      {data.items?.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {data.items.slice(0, 4).map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px]">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: statusColors[item.status] || '#9CA3AF' }} />
              <span className="text-[var(--text-secondary)] truncate flex-1">{item.kunden_name || '–'}</span>
              <span className="text-[10px] text-[var(--text-tertiary)] flex-shrink-0">{statusLabels[item.status] || item.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WidgetPackTimer({ data, t }) {
  if (!data) return <WidgetSkeleton />;
  return (
    <div className="flex flex-col h-full">
      <HeroKPI value={data.avg_effizienz ? `${data.avg_effizienz}%` : '—'} label={t('home.widget.pack_timer_avgEff', 'Ø Effizienz')} />
      <div className="flex gap-3 mt-2 text-[11px]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /><span className="font-mono font-bold text-content">{data.aktiv_laufend || 0}</span> <span className="text-[var(--text-tertiary)]">{t('home.widget.pack_timer_active', 'aktiv')}</span></span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#338edc]" /><span className="font-mono font-bold text-content">{data.auftraege_heute || 0}</span> <span className="text-[var(--text-tertiary)]">{t('home.widget.pack_timer_today', 'heute')}</span></span>
      </div>
      <div className="flex gap-3 mt-1 text-[11px]">
        <span className="text-green-600">{data.in_time || 0} in time</span>
        <span className="text-red-500">{data.over_time || 0} {t('packTimer.overTime', 'über SOLL')}</span>
      </div>
      <div className="mt-auto pt-2 border-t border-line text-[11px] text-[var(--text-tertiary)]">
        {data.stationen_besetzt || 0}/{data.stationen_gesamt || 0} {t('home.widget.pack_timer_stations', 'Stationen besetzt')}
      </div>
    </div>
  );
}

function WidgetFlexPool({ data, t, locale }) {
  if (!data) return <WidgetSkeleton />;
  return (
    <div className="flex flex-col h-full">
      <HeroKPI value={data.pool_size || 0} label={t('home.widget.flex_poolSize') || 'Pool Size'} />
      <div className="flex gap-3 mt-2 text-[11px]">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /><span className="font-mono font-bold text-content">+{data.neue_7t || 0}</span> <span className="text-[var(--text-tertiary)]">{t('home.widget.flex_newApplicants') || 'new (7d)'}</span></span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#F59E0B' }} /><span className="font-mono font-bold text-content">{data.offene_einsaetze || 0}</span> <span className="text-[var(--text-tertiary)]">{t('home.widget.flex_openAssignments') || 'open'}</span></span>
      </div>
      {data.naechster_einsatz && (
        <div className="mt-3 pt-2 border-t border-line">
          <div className="text-[11px] text-[var(--text-secondary)]">{t('home.widget.flex_nextAssignment') || 'Next assignment'}</div>
          <div className="text-xs font-medium text-content mt-0.5">{data.naechster_einsatz.titel}</div>
          <div className="text-[11px] text-[var(--text-tertiary)]">{data.naechster_einsatz.datum} &middot; {data.naechster_einsatz.zugesagt}/{data.naechster_einsatz.benoetigt}</div>
        </div>
      )}
    </div>
  );
}

const WIDGET_RENDERERS = {
  forecast: WidgetForecast,
  schichtplanung: WidgetSchichtplanung,
  kunden_rotation: WidgetKundenRotation,
  performance: WidgetPerformance,
  feiertage: WidgetFeiertage,
  mitarbeiter: WidgetMitarbeiter,
  rohdaten: WidgetRohdaten,
  api_config: WidgetApiConfig,
  quick_stats: WidgetQuickStats,
  ai_insight: WidgetAiInsight,
  top_performer: WidgetTopPerformer,
  besetzung: WidgetBesetzung,
  system_health: WidgetSystemHealth,
  notifications: WidgetNotifications,
  forecast_chart: WidgetForecastChart,
  segmentanalyse: WidgetSegmentanalyse,
  operations_heute: WidgetOperationsHeute,
  durchsatz: WidgetDurchsatz,
  cases_overview: WidgetCasesOverview,
  cases_watchlist: WidgetCasesWatchlist,
  cases_trend: WidgetCasesTrend,
  email_inbox_today: WidgetEmailToday,
  satisfaction_score: WidgetSatisfaction,
  active_substitutions: WidgetActiveSubstitutions,
  forecast_deadlines: WidgetForecastDeadlines,
  flex_pool: WidgetFlexPool,
  pack_timer: WidgetPackTimer,
};

/* ══════════════════════════════════════════════════════════════
   WIDGET WRAPPER (with drag handle, remove button, link)
   ══════════════════════════════════════════════════════════════ */

function WidgetCard({ widget, data, editMode, onRemove, onDragStart, onDragOver, onDrop, onDragEnd, onRefreshAi, t, locale, tMonth }) {
  const meta = WIDGET_META[widget.id] || {};
  const Renderer = WIDGET_RENDERERS[widget.id];
  const labelKey = WIDGET_LABEL_KEYS[widget.id];
  const label = labelKey ? t(labelKey) : widget.id;
  const Icon = meta.icon;

  const inner = (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && (
              <Icon style={{ color: 'var(--text-secondary)', width: 16, height: 16 }} />
          )}
          <span className="text-xs font-semibold text-content-secondary">{label}</span>
        </div>
        {editMode && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
            className="w-6 h-6 flex items-center justify-center rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors text-xs font-bold z-10"
          >
            &times;
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {Renderer ? <Renderer data={data} onRefresh={widget.id === 'ai_insight' ? onRefreshAi : undefined} t={t} locale={locale} tMonth={tMonth} /> : <div className="text-xs text-[var(--text-tertiary)]">{t('home.widget_not_found')}</div>}
      </div>
    </div>
  );

  const cardClasses = `relative bg-surface-card border rounded-card p-card shadow-card transition-all duration-200 overflow-hidden min-h-[120px] ${
    editMode
      ? 'border-dashed border-primary-300 dark:border-primary-700 cursor-grab active:cursor-grabbing'
      : 'border-line hover:border-line-hover hover:shadow-card-hover'
  }`;

  /* ── AUTO-FLOW GRID: only use span for wide widgets, NO explicit x/y positioning ── */
  const gridStyle = {};
  if ((widget.w || 1) > 1) gridStyle.gridColumn = `span ${widget.w}`;
  if ((widget.h || 1) > 1) gridStyle.gridRow = `span ${widget.h}`;

  if (editMode) {
    return (
      <div
        className={cardClasses}
        style={gridStyle}
        draggable
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
      >
        {/* Drag handle */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 flex gap-0.5 opacity-40">
          <span className="w-1 h-1 rounded-full bg-content-dim" />
          <span className="w-1 h-1 rounded-full bg-content-dim" />
          <span className="w-1 h-1 rounded-full bg-content-dim" />
        </div>
        {inner}
      </div>
    );
  }

  // In view mode, linkable widgets wrap in NavLink
  if (meta.to) {
    return (
      <NavLink to={meta.to} className={`${cardClasses} group block`} style={gridStyle}>
        {inner}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={meta.color || '#888'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 17l10-10M7 7h10v10" />
          </svg>
        </div>
      </NavLink>
    );
  }

  return <div className={cardClasses} style={gridStyle}>{inner}</div>;
}

/* ══════════════════════════════════════════════════════════════
   WIDGET CATALOG MODAL
   ══════════════════════════════════════════════════════════════ */

function WidgetCatalog({ available, currentIds, onAdd, onClose, t }) {
  const grouped = {};
  available.forEach(w => {
    const cat = w.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(w);
  });

  return (
    <Modal open={true} onClose={onClose} title={t('home.add_widget_title')} size="md">
      <div className="space-y-5">
        {Object.entries(grouped).map(([cat, widgets]) => (
          <div key={cat}>
            <div className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-widest mb-2">{CATEGORY_LABEL_KEYS[cat] ? t(CATEGORY_LABEL_KEYS[cat]) : cat}</div>
            <div className="space-y-2">
              {widgets.map(w => {
                const alreadyAdded = currentIds.has(w.id);
                const wMeta = WIDGET_META[w.id] || {};
                const WIcon = wMeta.icon;
                return (
                  <button
                    key={w.id}
                    disabled={alreadyAdded}
                    onClick={() => { if (!alreadyAdded) onAdd(w); }}
                    className={`w-full text-left p-3 rounded-card border transition-all flex items-center gap-3 ${
                      alreadyAdded
                        ? 'border-line/50 opacity-40 cursor-default'
                        : 'border-line hover:border-line-hover hover:bg-surface-hover cursor-pointer'
                    }`}
                  >
                    {WIcon && (
                        <WIcon style={{ color: 'var(--text-secondary)', width: 16, height: 16 }} className="flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-content-secondary">{w.label}</div>
                      <div className="text-[11px] text-[var(--text-tertiary)]">{w.description}</div>
                    </div>
                    {alreadyAdded && <Badge variant="default" className="text-[10px] shrink-0">{t('home.active_badge')}</Badge>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

/* ══════════════════════════════════════════════════════════════
   Helper: fetch data for a single widget
   ══════════════════════════════════════════════════════════════ */

function fetchWidgetData(widgetId) {
  // Watchlist uses its own endpoint
  if (widgetId === 'cases_watchlist') {
    return fetch(`${API}/api/cases/my-watchlist?limit=5`, { credentials: 'include' })
      .then(r => r.json())
      .catch(() => null);
  }
  return fetch(`${API}/api/dashboard/widget-data/${widgetId}`, { credentials: 'include' })
    .then(r => r.json())
    .catch(() => null);
}

/* ══════════════════════════════════════════════════════════════
   MAIN DASHBOARD
   ══════════════════════════════════════════════════════════════ */

// ──── Role-based KPI sections ────
const ROLLE_LABEL_KEYS = { csm: 'home.rolle.csm', hr: 'home.rolle.hr', ops: 'home.rolle.ops', cfo: 'home.rolle.cfo' };

function RoleKpis({ rolle, t, locale }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/dashboard/rolle/${rolle}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setData(d.kpis); })
      .catch(() => {});
  }, [rolle]);

  if (!data) return null;

  if (rolle === 'csm') {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label={t('home.kpi.forecasts_complete')} value={`${data.forecasts_complete}/${data.forecasts_total}`} onClick={() => navigate('/forecast')} />
        <KpiCard label={t('home.kpi.missing_targets')} value={data.missing_vorgaben} onClick={() => navigate('/zeitvorgaben')} />
        <KpiCard label={t('home.kpi.next_deadline')} value={data.next_deadline ? new Date(data.next_deadline.deadline).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' }) : '–'} />
        <KpiCard label={t('home.kpi.open_tasks')} value={data.forecasts_total - data.forecasts_complete} />
      </div>
    );
  }

  if (rolle === 'hr') {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label={t('home.kpi.fte_demand')} value={data.fte_bedarf} />
        <KpiCard label={t('home.kpi.available')} value={data.fte_verfuegbar} />
        <KpiCard label={t('home.kpi.expiring_contracts')} value={data.expiring_contracts} onClick={() => navigate('/mitarbeiter')} />
        <KpiCard label={t('home.kpi.open_requests')} value={data.open_requests} onClick={() => navigate('/portal/urlaub')} />
      </div>
    );
  }

  if (rolle === 'ops') {
    const trend = data.trend_prozent;
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label={t('home.kpi.packages_today')} value={data.pakete_heute} trend={trend !== 0 ? `${trend > 0 ? '+' : ''}${trend}%` : undefined} trendUp={trend > 0} />
        <KpiCard label={t('home.kpi.shift_plan')} value={data.schichtplan_count > 0 ? t('home.kpi.created') : t('home.kpi.missing')} onClick={() => navigate('/schichtplanung')} />
        <KpiCard label={t('home.kpi.rotation')} value={`${data.rotation_prozent}%`} onClick={() => navigate('/kunden-rotation')} />
        <KpiCard label={t('home.kpi.current_week')} value={`KW ${data.aktuelle_kw}`} />
      </div>
    );
  }

  if (rolle === 'cfo') {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label={t('home.kpi.avg_margin')} value={`${data.marge_durchschnitt}%`} />
        <KpiCard label={t('home.kpi.below_target', { n: data.kunden_unter_ziel })} value={data.kunden_unter_ziel} onClick={() => navigate('/deckungsbeitrag')} />
      </div>
    );
  }

  return null;
}

export default function Home() {
  const { user } = useAuth();
  const { t, locale, tMonth } = useLanguage();
  const [health, setHealth] = useState(null);
  const [layout, setLayout] = useState(null);
  const [savedLayout, setSavedLayout] = useState(null);
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [widgetData, setWidgetData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiInsight, setAiInsight] = useState(null);
  const dragItem = useRef(null);

  // Load health — once
  useEffect(() => {
    fetch(`${API}/api/health`)
      .then(r => r.json())
      .then(setHealth)
      .catch(() => setHealth({ status: 'error' }));
  }, []);

  // Load dashboard config — once
  useEffect(() => {
    fetch(`${API}/api/dashboard/config`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        const l = d.layout;
        setLayout(l);
        setSavedLayout(l);
        loadAllWidgetData(l);
      })
      .catch(() => {});

    fetch(`${API}/api/dashboard/widgets`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setAvailableWidgets(d.widgets || []))
      .catch(() => {});
  }, []);

  // Batch-load widget data for a given layout (used only on init and reset)
  function loadAllWidgetData(widgets) {
    if (!widgets || !widgets.length) return;
    const batch = {};
    const fetches = widgets
      .filter(w => w.id !== 'ai_insight')
      .map(w =>
        fetchWidgetData(w.id).then(data => { if (data) batch[w.id] = data; })
      );

    if (widgets.some(w => w.id === 'ai_insight')) {
      loadAiInsight();
    }

    Promise.all(fetches).then(() => {
      setWidgetData(prev => ({ ...prev, ...batch }));
    });
  }

  function loadAiInsight() {
    setAiInsight(null);
    fetch(`${API}/api/dashboard/ai-insight`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } })
      .then(r => r.json())
      .then(d => setAiInsight(d))
      .catch(() => setAiInsight({ insight: t('home.ai_unavailable') }));
  }

  function loadSingleWidgetData(widgetId) {
    if (widgetId === 'ai_insight') {
      loadAiInsight();
      return;
    }
    fetchWidgetData(widgetId).then(data => {
      if (data) setWidgetData(prev => ({ ...prev, [widgetId]: data }));
    });
  }

  // ── Drag and drop: reorder array instead of swapping x/y positions ──

  const handleDragStart = useCallback((e, idx) => {
    dragItem.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e, targetIdx) => {
    e.preventDefault();
    const sourceIdx = dragItem.current;
    if (sourceIdx === null || sourceIdx === targetIdx) return;

    setLayout(prev => {
      if (!prev) return prev;
      const next = [...prev];
      const [moved] = next.splice(sourceIdx, 1);
      next.splice(targetIdx, 0, moved);
      return next;
    });
    dragItem.current = null;
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.target.style.opacity = '1';
    dragItem.current = null;
  }, []);

  // ── Remove widget ──

  const removeWidget = useCallback((idx) => {
    setLayout(prev => {
      if (!prev) return prev;
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  // ── Add widget: simply append to array ──

  const addWidget = useCallback((widgetDef) => {
    setLayout(prev => {
      if (!prev) return prev;
      const newWidget = { id: widgetDef.id, w: widgetDef.size === 'large' ? 2 : (widgetDef.w || 1), h: widgetDef.h || 1 };
      return [...prev, newWidget];
    });
    setShowCatalog(false);
    loadSingleWidgetData(widgetDef.id);
  }, []);

  // ── Save / Cancel / Reset ──

  const saveLayout = useCallback(async () => {
    setSaving(true);
    try {
      const currentLayout = layout;
      await fetch(`${API}/api/dashboard/config`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ layout: currentLayout }),
      });
      setSavedLayout(currentLayout);
      setEditMode(false);
    } catch {}
    setSaving(false);
  }, [layout]);

  const cancelEdit = useCallback(() => {
    setLayout(savedLayout);
    setEditMode(false);
  }, [savedLayout]);

  const resetLayout = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/dashboard/reset`, { method: 'POST', credentials: 'include' });
      const d = await res.json();
      setLayout(d.layout);
      setSavedLayout(d.layout);
      setEditMode(false);
      loadAllWidgetData(d.layout);
    } catch {}
  }, []);

  // ── Render ──

  if (!layout) {
    return (
      <div className="p-page flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const currentWidgetIds = new Set(layout.map(w => w.id));
  const datumStr = new Date().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="p-page">
      {/* Greeting + Edit Button */}
      <PageHeader
        title={`${getGreeting(t)}, ${user?.vorname || ''}`}
        subtitle={
          <span className="flex items-center gap-3">
            {datumStr}
            {health && (
              <Badge variant={health.status === 'ok' ? 'success' : 'error'} dot>
                {health.status === 'ok' ? t('home.system_online') : t('home.api_offline')}
              </Badge>
            )}
          </span>
        }
        className="mb-section"
        actions={
          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <Button variant="secondary" size="sm" icon={Plus} onClick={() => setShowCatalog(true)}
                  className="border-dashed border-primary-400 text-primary-500">
                  {t('home.add_widget')}
                </Button>
                <Button variant="ghost" size="sm" icon={RotateCcw} onClick={resetLayout}>{t('home.reset_default')}</Button>
                <Button variant="ghost" size="sm" onClick={cancelEdit}>{t('common.cancel')}</Button>
                <Button variant="primary" size="sm" onClick={saveLayout} loading={saving}>{t('common.save')}</Button>
              </>
            ) : (
              <Button variant="secondary" size="sm" icon={Settings} onClick={() => { setSavedLayout(layout); setEditMode(true); }}>
                {t('home.customize')}
              </Button>
            )}
          </div>
        }
      />

      {/* Role-based KPIs + Task Widget */}
      {user?.dashboard_rollen?.length > 0 && (
        <div className="flex gap-4 mb-4">
          {/* Left: Task Widget */}
          <div className="w-[360px] flex-shrink-0">
            <div className="sticky top-16">
              <TaskWidget />
            </div>
          </div>
          {/* Right: Role KPIs */}
          <div className="flex-1 min-w-0 space-y-3">
            {user.dashboard_rollen.map(rolle => (
              <div key={rolle}>
                <div className="text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider mb-2">
                  {ROLLE_LABEL_KEYS[rolle] ? t(ROLLE_LABEL_KEYS[rolle]) : rolle}
                </div>
                <RoleKpis rolle={rolle} t={t} locale={locale} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Widget Grid — AUTO-FLOW: array order = visual order, NO explicit x/y placement */}
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" style={{ gridAutoRows: 'minmax(120px, auto)' }}>
        {layout.map((widget, idx) => (
          <WidgetCard
            key={widget.id}
            widget={widget}
            data={widget.id === 'ai_insight' ? aiInsight : widgetData[widget.id]}
            editMode={editMode}
            onRemove={() => removeWidget(idx)}
            onDragStart={(e) => handleDragStart(e, idx)}
            onDragOver={handleDragOver}
            onDrop={(e) => { handleDrop(e, idx); e.target.style.opacity = '1'; }}
            onDragEnd={handleDragEnd}
            onRefreshAi={loadAiInsight}
            t={t}
            locale={locale}
            tMonth={tMonth}
          />
        ))}
      </div>

      {/* Edit mode hint */}
      {editMode && (
        <div className="mt-section text-center text-xs text-[var(--text-tertiary)]">
          {t('home.edit_hint')}
        </div>
      )}

      {/* Widget Catalog Modal */}
      {showCatalog && (
        <WidgetCatalog
          available={availableWidgets}
          currentIds={currentWidgetIds}
          onAdd={addWidget}
          onClose={() => setShowCatalog(false)}
          t={t}
        />
      )}
    </div>
  );
}
